import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  CustomForm,
  CustomFormItemConstantWidth,
  CustomSpinSelect,
  OptionSpin,
  Text,
  useText,
} from '@src/components';
import { Form, Row, Col, Button, notification } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  getVehicleFuelTypes,
  getVehicleDepartments,
  getVehicleCostCenter,
  getVehicleTypes,
  getVehicleAttributes,
  getVehicleLAddressGetList,
} from '@src/store/slice/addNewCarStore/addCarPlateDetailSlice';
import {
  getVehicleDetailList,
  postUpdateVehicle,
} from '@src/store/slice/vehicleManagementStore/vehicleManagementDetailSlice';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import '@src/styles/addNewCar/addCarSelectInformation.scss';
import { useLanguageChange } from '@src/hooks/useLanguageChange';

const isNumber = (value) => typeof value === 'number' && !isNaN(value);

const VehicleEditInformation = ({ vehicleDetailList, vehicleManagmentItem }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [api, contextHolder] = notification.useNotification();
  const isMobil = useMediaQuery({ query: '(max-width: 1199px)' });
  const {
    vehicleFuelTypes = [],
    vehicleDepartments = [],
    vehicleCostCenters = [],
    vehicleTypes = [],
    vehicleAttributes = [],
    vehicleLAddressGetList = [],
  } = useSelector((state) => state.addCarPlateDetail);

  const { selectedCustomer } = useSelector((state) => state?.customer);
  const { isAdmin } = useSelector((state) => state.auth);
  const { selectedUserId } = useSelector((state) => state.user);
  const { loading: loadingPostUpdateVehicle } = useSelector(
    (state) => state.vehicleManagementDetail,
  );
  const [isEditing, setIsEditing] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const transactionSuccessfullyCompleted2 = useText('transactionSuccessfullyCompleted2');
  const carInformation = useText('carInformation');
  const vehicleQualificationText = useText('vehicleQualification');
  const vehicleTypeText = useText('vehicleType');
  const departmentText = useText('department');
  const fuelTypeText = useText('fuelType2');
  const costCenterText = useText('costCenter');
  const edit2 = useText('edit2');

  const fetchData = async () => {
    if (isAdmin && selectedUserId) {
      await dispatch(getVehicleLAddressGetList({ customerId: selectedUserId }));
      await dispatch(getVehicleDepartments({ customerId: selectedUserId, vouId: 4 }));
      await dispatch(getVehicleCostCenter({ customerId: selectedUserId, vouId: 4 }));
    } else {
      await dispatch(getVehicleLAddressGetList());
      await dispatch(getVehicleDepartments({ vouId: 4 }));
      await dispatch(getVehicleCostCenter({ vouId: 4 }));
    }
    await dispatch(getVehicleTypes());
    await dispatch(getVehicleFuelTypes({ vouId: 4 }));
    await dispatch(getVehicleAttributes());
  };

  // These lists only load while they are still empty, so their backend labels (vehicle type,
  // qualification, fuel type...) would stay in the previous language until a manual page refresh.
  useLanguageChange(() => {
    fetchData();
  });

  useEffect(() => {
    if (
      [
        vehicleDepartments,
        vehicleCostCenters,
        vehicleTypes,
        vehicleLAddressGetList,
        vehicleFuelTypes,
      ].every((item) => item.length === 0)
    ) {
      fetchData();
    }
  }, [
    dispatch,
    vehicleDepartments.length,
    vehicleCostCenters.length,
    vehicleTypes.length,
    vehicleLAddressGetList.length,
    vehicleFuelTypes.length,
  ]);

  useEffect(() => {
    if (
      [vehicleTypes, vehicleLAddressGetList, vehicleFuelTypes].every((item) => item.length !== 0)
    ) {
      if (vehicleDetailList) {
        form.setFieldsValue({
          deliveryAddressId: vehicleLAddressGetList.find(
            (address) => address.id === vehicleDetailList.deliveryAddressId,
          )?.name,
          segmentId: vehicleFuelTypes.find(
            (fuel) => fuel.fuelTypeId === vehicleDetailList.segmentId,
          )?.fuelTypeLabel,
          vehicleType: vehicleTypes.find(
            (type) => type.vehicleTypeId === vehicleDetailList.vehicleType,
          )?.vehicleTypeName,
          vehicleUsageType: vehicleAttributes.find(
            (attribute) => attribute.vehicleAttributeId === vehicleDetailList.vehicleUsageType,
          )?.vehicleAttributeName,
        });
      }
    }
    // The lists themselves (not just their length) are the dependency: a language switch replaces
    // them with the same number of entries under new labels, and these fields hold the label.
  }, [
    vehicleDetailList,
    vehicleTypes,
    vehicleLAddressGetList,
    vehicleFuelTypes,
    vehicleAttributes,
    form,
  ]);

  useEffect(() => {
    if ([vehicleDepartments].every((item) => item.length !== 0)) {
      if (vehicleDetailList) {
        form.setFieldsValue({
          departmanId: vehicleDepartments.find(
            (department) => department.id === vehicleDetailList.departmentId,
          )?.label,
        });
      }
    }
  }, [vehicleDetailList, vehicleDepartments, form]);

  useEffect(() => {
    if ([vehicleCostCenters].every((item) => item.length !== 0)) {
      if (vehicleDetailList) {
        form.setFieldsValue({
          costCenterId: vehicleCostCenters.find(
            (center) => center.costCenterId === vehicleDetailList.costCenterId,
          )?.costCenterLabel,
        });
      }
    }
  }, [vehicleDetailList, vehicleCostCenters, form]);

  useEffect(() => {
    setTimeout(() => {
      try {
        setPageLoading(false);
      } catch (error) {
        console.error(error);
      }
    }, 4000);
  }, []);

  const handleValuesChange = (changedValues, allValues) => {
    console.log('Form value changed: ', { ...allValues });
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    form
      .validateFields()
      .then(async (values) => {
        await vehicleDetailList?.vehicleDeviceTypes?.map(async (item, index) => {
          const request = {
            vehicles: [
              {
                vehicleId: vehicleDetailList.vehicleId,
                vehicleDetailId: item.vehicleDetailId,
                providerType: item.providerType,
                deliveryAddressId: isNumber(values.deliveryAddressId)
                  ? values.deliveryAddressId
                  : vehicleDetailList.deliveryAddressId,
                vehicleType: isNumber(values.vehicleType)
                  ? values.vehicleType
                  : vehicleDetailList.vehicleType,
                costCenterId: isNumber(values.costCenterId)
                  ? values.costCenterId
                  : vehicleDetailList.costCenterId,
                departmentId: isNumber(values.departmanId)
                  ? values.departmanId
                  : vehicleDetailList.departmentId,
                vehicleUsageType: isNumber(values.vehicleUsageType)
                  ? values.vehicleUsageType
                  : vehicleDetailList.vehicleUsageType,
                segmentId: isNumber(values.segmentId)
                  ? values.segmentId
                  : vehicleDetailList.segmentId,
                limitType: null,
                limitValueType: null,
              },
            ],
          };
          const response = await dispatch(postUpdateVehicle({ request })).unwrap();
          if (index === vehicleDetailList.vehicleDeviceTypes.length - 1) {
            if (response?.isSuccess) {
              api.success({
                message: carInformation,
                description: transactionSuccessfullyCompleted2,
                duration: 2,
              });
              await dispatch(
                getVehicleDetailList({
                  customerId:
                    isAdmin && selectedUserId ? selectedUserId : selectedCustomer.customerId,
                  id: vehicleDetailList.vehicleId,
                  providerType: vehicleDetailList.providerType,
                }),
              );
            }
            setIsEditing(false);
          }
        });
      })
      .catch((info) => {
        console.log('Validate Failed:', info);
      });
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  return (
    <div
      className="add-car-select-information"
      style={{ backgroundColor: '#fff', padding: '0px 8px 0px', borderRadius: '10px' }}
    >
      {contextHolder}
      <Row
        justify={'space-between'}
        align="middle"
        className="header-row-title-2"
        style={{ backgroundColor: '#fff' }}
      >
        {!isEditing ? (
          <span style={{ color: '#00a483' }}>
            <Text t="carInformation" />
          </span>
        ) : (
          ''
        )}
        {isEditing ? (
          <div style={{ display: 'flex', flex: 'auto', justifyContent: 'end' }}>
            <Button
              type="default"
              style={{ backgroundColor: '#ff4d4f', color: '#fff', marginRight: '10px' }}
              onClick={handleCancel}
            >
              <Text t="giveUp" />
            </Button>
            <Button
              type="primary"
              style={{ backgroundColor: '#00a483', color: '#fff' }}
              loading={loadingPostUpdateVehicle.postUpdateVehicle}
              disabled={loadingPostUpdateVehicle.postUpdateVehicle}
              onClick={handleSave}
            >
              <Text t="cardParameterSave" />
            </Button>
          </div>
        ) : (
          <Button
            type="text"
            icon={<EditOutlined style={{ color: '#0f172a', fontSize: '24px' }} />}
            onClick={handleEditClick}
            style={{ display: 'flex', alignItems: 'end' }}
          >
            {!isMobil ? edit2 : ''}
          </Button>
        )}
      </Row>
      <CustomForm
        name={`vehicleDetails-edit`}
        className="filter-form"
        autoComplete="off"
        initialValues={{}}
        layout={!isMobil ? 'horizontal' : 'vertical'}
        form={form}
        onValuesChange={handleValuesChange}
      >
        <Row gutter={{ xs: 16, sm: 16, md: 24, lg: 24 }} className="form-item">
          <Col span={isMobil ? 24 : 12}>
            <CustomFormItemConstantWidth
              label={
                <span>
                  <Text t="vehicleType" />
                </span>
              }
              name="vehicleType"
              className={'delivery-address-county-form-item'}
              style={{ columnGap: '1.5em' }}
            >
              <CustomSpinSelect
                id="add-car-plate-detail-vehicleType-select"
                placeholder={vehicleTypeText}
                disabled={!isEditing || vehicleTypes.length === 0}
                loading={vehicleTypes.length === 0}
                height={36}
              >
                {vehicleTypes?.map((vehicle) => (
                  <OptionSpin
                    id={`vehicle-edit-info-vehicleType-${vehicle?.vehicleTypeName}-option`}
                    key={vehicle?.vehicleTypeId}
                    value={vehicle?.vehicleTypeId}
                  >
                    {vehicle?.vehicleTypeName}
                  </OptionSpin>
                ))}
              </CustomSpinSelect>
            </CustomFormItemConstantWidth>
          </Col>

          <Col span={isMobil ? 24 : 12}>
            {!(isEditing && vehicleDepartments.length === 0) && (
              <CustomFormItemConstantWidth
                label={<Text t="department" />}
                name="departmanId"
                className={'delivery-address-county-form-item'}
                style={{ columnGap: '1.7em' }}
              >
                <CustomSpinSelect
                  id="add-car-plate-detail-departmanId-select"
                  placeholder={departmentText}
                  disabled={!isEditing || vehicleDepartments.length === 0}
                  loading={vehicleDepartments.length === 0 && pageLoading}
                  height={36}
                >
                  {vehicleDepartments?.map((vehicle) => (
                    <OptionSpin
                      id={`add-car-plate-detail-departmanId-${vehicle?.label}-option`}
                      key={vehicle?.id}
                      value={vehicle?.id}
                    >
                      {vehicle?.label}
                    </OptionSpin>
                  ))}
                </CustomSpinSelect>
              </CustomFormItemConstantWidth>
            )}
          </Col>

          <Col span={isMobil ? 24 : 12}>
            <CustomFormItemConstantWidth
              label={
                <span>
                  <Text t="vehicleQualification" />
                </span>
              }
              name="vehicleUsageType"
              className={'delivery-address-county-form-item'}
            >
              <CustomSpinSelect
                id="add-car-plate-detail-vehicleUsageType-select"
                placeholder={vehicleQualificationText}
                disabled={!isEditing || vehicleAttributes.length === 0}
                loading={vehicleAttributes.length === 0}
                height={36}
              >
                {vehicleAttributes?.map((vehicle) => (
                  <OptionSpin
                    id={`add-car-plate-detail-vehicleUsageType-${vehicle?.vehicleAttributeName}-option`}
                    key={vehicle?.vehicleAttributeId}
                    value={vehicle?.vehicleAttributeId}
                  >
                    {vehicle?.vehicleAttributeName}
                  </OptionSpin>
                ))}
              </CustomSpinSelect>
            </CustomFormItemConstantWidth>
          </Col>

          <Col span={isMobil ? 24 : 12}>
            <CustomFormItemConstantWidth
              label={<span>{useText('deliveryAddress2')}</span>}
              name="deliveryAddressId"
              className={'delivery-address-county-form-item'}
            >
              <CustomSpinSelect
                id="add-car-plate-detail-deliveryAddressId-select"
                placeholder={useText('deliveryAddress2')}
                disabled={!isEditing || vehicleLAddressGetList.length === 0}
                loading={vehicleLAddressGetList.length === 0 && pageLoading}
                height={36}
              >
                {vehicleLAddressGetList?.map((vehicle) => (
                  <OptionSpin
                    id={`add-car-plate-detail-deliveryAddressId-${vehicle?.name}-option`}
                    key={vehicle?.id}
                    value={vehicle?.id}
                  >
                    {vehicle?.name}
                  </OptionSpin>
                ))}
              </CustomSpinSelect>
            </CustomFormItemConstantWidth>
          </Col>
          <Col span={isMobil ? 24 : 12}>
            <CustomFormItemConstantWidth
              label={
                <span>
                  <Text t="fuelType2" />
                </span>
              }
              name="segmentId"
              className={'delivery-address-add-form-item'}
              style={{ columnGap: '1.5em' }}
            >
              <CustomSpinSelect
                id="add-car-plate-detail-fuelTypeId-select"
                placeholder={fuelTypeText}
                disabled={!isEditing || vehicleFuelTypes.length === 0}
                loading={vehicleFuelTypes.length === 0}
                height={36}
              >
                {vehicleFuelTypes?.map((vehicle) => (
                  <OptionSpin
                    id={`add-car-plate-detail-fuelTypeId-${vehicle?.fuelTypeLabel}-option`}
                    key={vehicle?.fuelTypeId}
                    value={vehicle?.fuelTypeId}
                  >
                    {vehicle?.fuelTypeLabel}
                  </OptionSpin>
                ))}
              </CustomSpinSelect>
            </CustomFormItemConstantWidth>
          </Col>
          <Col span={isMobil ? 24 : 12}>
            {!(isEditing && vehicleCostCenters.length === 0) && (
              <CustomFormItemConstantWidth
                label={<Text t="costCenter" />}
                name="costCenterId"
                className={'delivery-address-county-form-item'}
              >
                <CustomSpinSelect
                  id="add-car-plate-detail-costCenterId-select"
                  placeholder={costCenterText}
                  disabled={!isEditing || vehicleCostCenters.length === 0}
                  loading={vehicleCostCenters.length === 0 && pageLoading}
                  height={36}
                >
                  {vehicleCostCenters?.map((vehicle) => (
                    <OptionSpin
                      id={`add-car-plate-detail-costCenterId-${vehicle?.costCenterLabel}-option`}
                      key={vehicle?.costCenterId}
                      value={vehicle?.costCenterId}
                    >
                      {vehicle?.costCenterLabel}
                    </OptionSpin>
                  ))}
                </CustomSpinSelect>
              </CustomFormItemConstantWidth>
            )}
          </Col>
        </Row>
      </CustomForm>
    </div>
  );
};

VehicleEditInformation.propTypes = {
  vehicleDetailList: PropTypes.object,
  vehicleManagmentItem: PropTypes.object,
};

export default VehicleEditInformation;
